import moment from 'moment'
import nesProp from 'nested-property'
import i18n from 'lib/i18n'
import * as c from 'state/constants'
import phoneCodes from '../phoneCountryCodesSelect.json'
import { getCountOfGeneratedDocumentsWhatRequireSigning, isATarificationAndHasAMarkedTemplate, isMandateAndHasAMarkedTemplate } from './DSWizardHelper'
import { isAnEmail } from 'lib/validator'

// init translations
const _ = i18n('dswizard')

/**
 * This function will set state for the broker selector input.
 * Insert the list of broker like an array contains plain JS objects (label, value).
 * @param {*} brokers
 * @returns
 */
export const getBrokersInOptionList = (brokers) => {
    const brokerObjKeys = brokers && Object.keys(brokers) || []
    const brokerOptions = brokerObjKeys.map((brokerKey) => ({
        value: brokers[brokerKey].brokerNumber,
        label: brokers[brokerKey].brokerNumber + " - " + brokers[brokerKey].main.name
    }))

    return brokerOptions
}

/**
 * This function will set state for the broker selector input.
 * Insert the list of broker like an array contains plain JS objects (label, value).
 * @param {*} brokerNumber
 * @param {*} brokers
 * @returns
 */
export const getBrokerEmailsInOptionList = (brokerNumber, brokers, userId) => {
    const brokerObjKeys = brokers && Object.keys(brokers) || []

    let broker = {}
    brokerObjKeys.forEach((brokerKey) => {
        if (brokers[brokerKey].brokerNumber + "" === brokerNumber + "") {
            broker = brokers[brokerKey]
        }
    })

    let brokerEmailOptions = nesProp.get(broker, "main.emails") || []

    brokerEmailOptions = brokerEmailOptions.map((item) => ({
        value: item,
        label: item
    }))

    let staffList = nesProp.get(broker, "staff")
    if (staffList && staffList.length > 0) {
        staffList.forEach((oneStaff) => {
            if (oneStaff.userId == userId) {
                brokerEmailOptions.push({
                    value: oneStaff.email,
                    label: oneStaff.email
                })
            }
        })
    }

    //remove duplicate entries
    brokerEmailOptions = brokerEmailOptions.filter((listElement, pos, self) => pos == self.findIndex(email => email.value == listElement.value))

    return brokerEmailOptions
}

/**
* This function attach a temporal ID for the dynamic documents in form to be able to
* handle the already saved with the new ones
* @param {*} packageDSDocuments
* @param {*} dynamicFieldIdCursor
* @param {*} resetAllCursor - If this is true then will reset a new incremental order for cursors for al documents
* without any exception, else just the newly added documents will give an new cursor in the list.
* @returns Object:  {packageDSDocuments: <array with objects>, dynamicFieldIdCursor: <number>}
*/
export const packageDocumentsAttachIdCursor = (packageDSDocuments, dynamicFieldIdCursor, resetAllCursor = false) => {
    packageDSDocuments = packageDSDocuments.map((oneDoc) => {
        if (resetAllCursor || !oneDoc.cursor) {
            oneDoc.cursor = dynamicFieldIdCursor
            dynamicFieldIdCursor += 1
        }
        return oneDoc
    })

    return { packageDSDocuments, dynamicFieldIdCursor }
}

/**
* This function will update the Array elements. Find document by _id and merge it in the Array by new record object.
* @param {*} packageDSDocuments
* @param {*} dsDocument
* @returns Array with dsDocument record objects
*/
export const packageDocumentsListUpdateByDocument = (packageDSDocuments, dsDocument) => {
    if (!dsDocument) {
        console.warn("Can not update packageDSDocuments list with empty object instead of dsDocument record. (package-DocumentsListUpdateByDocument)")
        return packageDSDocuments
    }

    if (!packageDSDocuments) {
        packageDSDocuments = []
        packageDSDocuments.push(dsDocument)
        return packageDSDocuments
    }

    packageDSDocuments.forEach((oneDoc, index) => {
        if (String(oneDoc._id) === String(dsDocument._id)) {
            packageDSDocuments[index] = {
                ...packageDSDocuments[index],
                ...dsDocument
            }
        }
    })

    return packageDSDocuments
}

/**
 * Get all of the client emails in a list what is digestible for react select.
 * @param {*} clientDataExt
 * @param {*} clientId
 * @returns sorted Array: emails = [{label, value, default}]
 */
export const getClientEmailsInOptionList = (clientDataExt, clientId) => {
    let emails = nesProp.get(clientDataExt, clientId + '.detail.emails')

    if (emails && emails.length) {
        emails = emails.map((item) => ({
            referenceId: clientId,
            label: item.email,
            value: item.email,
            default: (item.default == "true" || item.default == true) ? true : false
        }))

        emails = emails.sort((item, item2) => {
            if (item.default && !item2.default) { return -1 }
            if (!item.default && item2.default) { return 1 }
            if ((item.default && item2.default) || (!item.default && !item2.default)) { return 0 }
        }) || []
    }

    return emails
}

/**
 * Get all of the client gsm phone numbers in a list what is digestible for react select.
 * @param {*} clientDataExt
 * @param {*} clientId
 * @returns sorted Array: phoneNumber = [{label, value, default}]
 */
export const getClientGSMPhoneNumbers = (clientDataExt, clientId) => {
    const phoneNumbers = nesProp.get(clientDataExt, clientId + '.detail.phoneNumbers')
    let mobilePhoneNumbers = []

    if (phoneNumbers) {
        mobilePhoneNumbers = phoneNumbers.filter(number => number.mobile && (number.mobile == 'true' || number.mobile == true))
    }

    if (mobilePhoneNumbers && mobilePhoneNumbers.length > 0) {
        mobilePhoneNumbers = mobilePhoneNumbers.map(item => {
            const spreadNumber = spreadPhoneNumber(item.number)
            return ({
                label: spreadNumber.phoneNumber,
                value: spreadNumber.phoneNumber,
                default: (item.default === 'true' ? true : false),
                countryCode: spreadNumber.phoneCode
            })
        })

        mobilePhoneNumbers = mobilePhoneNumbers.sort((item1, item2) => {
            if (item1.default && !item2.default) { return -1 }
            if (!item1.default && item2.default) { return 1 }
            if ((item1.default && item2.default) || (!item1.default && !item2.default)) { return 0 }
        }) || []
    }

    return mobilePhoneNumbers
}

/**
 *
 * @param {*} arr
 * @returns
 */
const removeDuplicates = (arr) => {
    const uniqueValues = []
    const visited = {}

    for (let i = 0; i < arr.length; i++) {
        const value = arr[i]
        if (!visited[value]) {
            visited[value] = true
            uniqueValues.push(value)
        }
    }

    return uniqueValues;
}


/**
 * Get all of the client gsm phone numbers in a list what is digestible for react select.
 * @param {*} clientDataExt
 * @param {*} clientId
 * @returns sorted Array: phoneNumber = [{label, value, default}]
 */
export const getBrokerGSMPhoneNumbers = (phoneNumbers, userPhoneNumber = null) => {
    let mobilePhoneNumbers = []

    if (phoneNumbers) {
        // we have no information about number is mobile or not
        // TODO find a solution for this
        mobilePhoneNumbers = phoneNumbers //.filter(number => number.mobile && (number.mobile == 'true' || number.mobile == true))
    }
    let spreadUserNumber = null

    if (userPhoneNumber) {
        mobilePhoneNumbers.push(userPhoneNumber)
        mobilePhoneNumbers = removeDuplicates(mobilePhoneNumbers)
        spreadUserNumber = spreadPhoneNumber(userPhoneNumber)
    }
    mobilePhoneNumbers = removeDuplicates(mobilePhoneNumbers)
    if (mobilePhoneNumbers && mobilePhoneNumbers.length > 0) {
        mobilePhoneNumbers = mobilePhoneNumbers.map((item, index) => {
            const spreadNumber = spreadPhoneNumber(item)
            return ({
                label: spreadNumber.phoneNumber,
                value: spreadNumber.phoneNumber,
                default: ((spreadUserNumber && spreadUserNumber.phoneNumber === spreadNumber.phoneNumber) ? true : false),
                countryCode: spreadNumber.phoneCode
            })
        })

        // if no default exists select the first
        if (!mobilePhoneNumbers.some(item => item.default)) {
            mobilePhoneNumbers[0].default = true
        }

        mobilePhoneNumbers = mobilePhoneNumbers.sort((item1, item2) => {
            if (item1.default && !item2.default) { return -1 }
            if (!item1.default && item2.default) { return 1 }
            if ((item1.default && item2.default) || (!item1.default && !item2.default)) { return 0 }
        }) || []
    }

    return mobilePhoneNumbers
}

/**
 * This function extracts country code from phone number and returns a object wit both values
 * @param {*} phoneNumber
 * @returns
 */
export const spreadPhoneNumber = phoneNumber => {
    //check if phone number starts with "00" or "+" than normalize it if necessary
    if (phoneNumber && phoneNumber.trim()[0] === "+") {
        phoneNumber = "00" + phoneNumber.substring(1)
    }

    //declare a default result for invalid values
    const result = {
        phoneNumber: '',
        phoneCode: '+32' //default Belgium value
    }

    // if the phone number contains other characters than numbers, return default
    const isNumber = /^\d+$/.test(phoneNumber)
    if (!isNumber) {
        return result
    }

    result.phoneNumber = phoneNumber
    //Find the country phone code in our list and store it in a variable
    let maxTestPhoneCodeLength = 6
    const countryCode = phoneNumber.slice(0, maxTestPhoneCodeLength)
    let foundCodeInList = false
    for (let code of phoneCodes) {
        if (countryCode.startsWith(code.phoneCode)) {
            result.phoneCode = code.phoneCode
            foundCodeInList = true
        }
    }

    //reduce the phone number to its value without the country code to normalize all numbers
    if (result.phoneCode && result.phoneCode.length > 0 && foundCodeInList) {
        result.phoneNumber = result.phoneNumber.slice(result.phoneCode.length)
    }

    if (result.phoneNumber[0] != 0) {
        result.phoneNumber = '0' + result.phoneNumber
    }

    //set phone code to '+' format
    if (result.phoneCode[0] !== '+') {
        result.phoneCode = '+' + result.phoneCode.substring(2)
    }

    return result
}

/**
 * Gets the list for the country phone codes
 * @returns Options list of label and values keys objects for react select2 to handle
 */
export const getCountryPhoneCodes = () => {
    const countryCodesOptionsList = []
    for (let code of phoneCodes) {
        const selectOption = {
            label: `+${code.phoneCode.substring(2)}`,
            value: '+' + code.phoneCode.substring(2),
            default: code.phoneCode == '0032' ? true : false
        }
        countryCodesOptionsList.push(selectOption)
    }
    countryCodesOptionsList.sort((a, b) => (a.value > b.value) ? 1 : ((a.value < b.value) ? -1 : 0))
    return countryCodesOptionsList
}

/**
 * This function will provide a blank frame of the schema for the form
 */
export const getNewDSDocumentInstance = (security) => {
    let oneInstanceFrame = nesProp.get(security, "DigitalSignatureHardLists.DSDocumentSchemaProps")
    // copy object props
    oneInstanceFrame = JSON.parse(JSON.stringify(oneInstanceFrame))

    return oneInstanceFrame
}


/**
 * This function wil retrieve contract and return
 */
export const findContractInClientObj = (client, contractId) => {

    let result = null
    let contracts = nesProp.get(client, "contracts")

    if (!contracts || contracts.length === 0) {
        return result
    }

    let objKeys = Object.keys(contracts)

    // fishing our contract by ID from stack
    objKeys && objKeys.map((oneKey) => {
        let oneContractId = nesProp.get(contracts, oneKey + ".contractMain.contractId")

        // got it and save into the result
        if (oneContractId === contractId) {
            result = nesProp.get(contracts, oneKey)
        }
    })

    return result
}

/**
* This function will return broker number by contract or the first from the list
* @param {*} contractInfo
*/
export const getBrokerNumberByContract = (contractInfo, brokers, contractDetails) => {
    let result
    if (contractDetails) {
        result = contractDetails.brokerNumber || null
    }

    if (!result && contractDetails) {
        result = contractInfo && contractInfo.brokerNumber || null
    }

    // if no contract related brokerNumber then get the first one
    if (!result) {
        const brokerObjKeys = brokers && Object.keys(brokers) || []
        if (brokerObjKeys.length > 0) {
            result = brokers[brokerObjKeys[0]].brokerNumber
        }
    }

    return result
}

/**
* This function will return broker info by brokerNumber
* @param {*} brokerNumber
*/
export const getBrokerFullInfoByNumber = (brokerNumber, brokers) => {
    const brokerObjKeys = brokers && Object.keys(brokers) || []
    let result = null

    if (brokerObjKeys.length > 0) {
        brokerObjKeys.forEach((oneBrokerKey) => {
            if (brokers[oneBrokerKey].brokerNumber === brokerNumber) {
                result = brokers[oneBrokerKey]
            }
        })
    }

    return result
}

/**
* This function will return remainder interval selected default
*/
export const getDefaultRemainderInterval = (security, DSDocumentsConfig) => {
    // const { security, DSDocumentsConfig } = this.props

    return nesProp.get(DSDocumentsConfig, "remainderInterval") ||
        nesProp.get(security, "DigitalSignatureHardLists.DigitalSignatureRemainderIntervals.0.value")
}

/**
* This function will return remainder interval selected default
*/
export const getDefaultRemainderRepeats = (security, DSDocumentsConfig) => {
    // const { security, DSDocumentsConfig } = this.props

    return nesProp.get(DSDocumentsConfig, "remainderRepeats") ||
        nesProp.get(security, "DigitalSignatureHardLists.DigitalSignatureRemainderIntervalRepeat.3.value") ||
        nesProp.get(security, "DigitalSignatureHardLists.DigitalSignatureRemainderIntervalRepeat.0.value")
}

/**
* This function will return remainder interval selected default
*/
export const getDefaultTarificationRemainderInterval = (security, DSDocumentsConfig) => {
    // const { security, DSDocumentsConfig } = this.props

    return nesProp.get(DSDocumentsConfig, "remainderTicketInterval") ||
        nesProp.get(security, "DigitalSignatureHardLists.DigitalSignatureRemainderIntervals.0.value")
}

/**
* This function will return remainder interval selected default
*/
export const getDefaultTarificationRemainderRepeats = (security, DSDocumentsConfig) => {
    // const { security, DSDocumentsConfig } = this.props

    return nesProp.get(DSDocumentsConfig, "remainderTicketRepeats") ||
        nesProp.get(security, "DigitalSignatureHardLists.DigitalSignatureRemainderIntervalRepeat.3.value") ||
        nesProp.get(security, "DigitalSignatureHardLists.DigitalSignatureRemainderIntervalRepeat.0.value")
}

/**
* This function will return expiration on date default
*/
export const getDefaultExpirationOn = (DSDocumentsConfig) => {
    //return new Date().setFullYear(new Date().getFullYear() + 1)
    // Calendar time displays time relative to a given referenceDay (defaults to the start of today).

    let defExpDay = nesProp.get(DSDocumentsConfig, "packageExpiration.day")
    let defExpMonth = nesProp.get(DSDocumentsConfig, "packageExpiration.month")
    let defExpYear = nesProp.get(DSDocumentsConfig, "packageExpiration.year")

    return moment()
        .add(moment.duration(defExpDay, 'days'))
        .add(moment.duration(defExpMonth, 'months'))
        .add(moment.duration(defExpYear, 'years'))
        .endOf('day').format("YYYY-MM-DD")
}


/**
 * This function will toggle show the second signer fields and set or reset values
 * @param {*} formInputs
 * @param {*} show
 * @returns formInputs
 */
export const showSecondSignerFields = (formInputs, show, requireSmsOtpSigning) => {
    const secondSignerFields = ["secondSignerLastName", "secondSignerFirstName", "secondSignerEmailAddress", "secondSignerLanguage"]

    if (requireSmsOtpSigning) {
        secondSignerFields.push('secondSignerPhoneNumber', 'secondSignerCountryPhoneCode')
    } else {
        formInputs['secondSignerPhoneNumber'].isRequired = false
        formInputs['secondSignerPhoneNumber'].value = null
        formInputs['secondSignerPhoneNumber'].touched = false
        formInputs['secondSignerPhoneNumber'].isValid = () => (true)
        formInputs['secondSignerCountryPhoneCode'].isRequired = false
        formInputs['secondSignerCountryPhoneCode'].value = null
        formInputs['secondSignerCountryPhoneCode'].touched = false
    }


    if (!show) {
        for (const field of secondSignerFields) {
            formInputs[field].isRequired = false
            formInputs[field].value = null
        }
    } else {
        for (const field of secondSignerFields) {
            formInputs[field].isRequired = true
            // formInputs[field].value = ''

            //set default validation for belgium numbers
            if (field === 'secondSignerPhoneNumber') {
                formInputs[field].isValid = (value) => (value[0] == '0' ? value.length === 10 : value.length === 9)
            }

            // set default country code for belgium
            if (field === 'secondSignerCountryPhoneCode') {
                if (!formInputs[field].value) {
                    formInputs[field].value = '+32'
                }
            }
        }
    }

    return formInputs
}

/**
 * This function will toggle show the second signer fields and set or reset values
 * @param {*} brokerSigningStakeholder
 * @param {*} formInputs
 * @param {*} brokerInfo
 * @param {*} lang
 * @param {*} countryPhoneCodes
 * @param {*} show
 * @param {*} requireSmsOtpSigning
 * @param {*} userFirstName
 * @param {*} userLastName
 * @returns formInputs
 */
export const resetBrokerSignerRelatedFields = (brokerSigningStakeholder, formInputs, brokerInfo, lang, countryPhoneCodes, show, requireSmsOtpSigning,
    userFirstName, userLastName, userPhoneNumber) => {
    const fieldNameList = ["brokerSignerLastName", "brokerSignerFirstName", /* "brokerSignerBirthDate", */ "brokerSignerLanguage",
        "brokerSignerPhoneNumber", "brokerSignerCountryPhoneCode"]

    // set broker as signer or not
    formInputs["brokerSignerExists"].value = show

    for (const fieldName of fieldNameList) {
        formInputs[fieldName].isRequired = show
        formInputs[fieldName].touched = false
    }

    // set default values from brokerInfo
    let code = countryPhoneCodes.find(oneCode => (oneCode.default ? true : false))
    if (!code) { code = countryPhoneCodes[0] }

    let spreadUsersPhone = spreadPhoneNumber(brokerSigningStakeholder ? brokerSigningStakeholder.phoneNumber : userPhoneNumber)

    formInputs["brokerSignerFirstName"].value = userFirstName
    formInputs["brokerSignerLastName"].value = userLastName

    formInputs["brokerSignerLanguage"].value = lang
    //formInputs["brokerSignerBirthDate"].value = moment().subtract(25, 'years').format("YYYY/MM/DD") // pick a random date

    formInputs["brokerSignerPhoneNumber"].value = spreadUsersPhone ? spreadUsersPhone.phoneNumber : brokerInfo.main.phoneNumbers[0]
    formInputs["brokerSignerCountryPhoneCode"].value = spreadUsersPhone ? spreadUsersPhone.phoneCode : code.value

    if (show) {
        formInputs["brokerSignerPhoneNumber"].isValid = (value) => (value[0] == '0' ? value.length === 10 : value.length === 9)
    } else {
        formInputs["brokerSignerPhoneNumber"].isValid = () => true
    }

    // check if sms required
    if (requireSmsOtpSigning) {
        formInputs['brokerSignerPhoneNumber'].isRequired = show
        formInputs['brokerSignerCountryPhoneCode'].isRequired = show
    } else {
        formInputs['brokerSignerPhoneNumber'].isRequired = false
        formInputs['brokerSignerCountryPhoneCode'].isRequired = false
    }

    return formInputs
}

/**
 * One input field basic props for handle form.
 */
export const _inputPrototype = (inputName) => {
    return {
        label: "",
        value: "",
        touched: false,
        error: false,
        isRequired: false,
        isValid: () => (true),
        errorMessage: _("invalidInputValue"),
        errorMessageRequired: _("requiredInputValueFor" + inputName)
    }
}

/**
 * Copy one input handler object and return.
 */
export const _initValueOfInput = (inputName) => {
    let proto = JSON.parse(JSON.stringify(_inputPrototype(inputName)))
    proto.isValid = () => (true) // JSON trick is not perfect to copy an object :)
    return proto
}


/**
 * This function will generate a name for the field of DSDocumentTemplate in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocTemplate = (cursor) => {
    return "packageDocumentTemplate" + cursor
}

/**
 * This function will generate a name for the field of DSDocument Language in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocLang = (cursor) => {
    return "packageDocumentLang" + cursor
}

/**
 * This function will generate a name for the field of DSDocument team in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocTeam = (cursor) => {
    return "packageDocumentTeam" + cursor
}

/**
 * This function will generate a name for the field of DSDocument team name in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocTeamName = (cursor) => {
    return "packageDocumentTeamName" + cursor
}

/**
 * This function will generate a name for the field of DSDocument team name in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocTeamSubgroup = (cursor) => {
    return "packageDocumentTeamSubgroupName" + cursor
}

/**
 * This function will generate a name for the field of DSDocument Contract in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocContractNumber = (cursor) => {
    return "packageDocumentContractNumber" + cursor
}

/**
 * This function will generate a name for the field of DSDocument Contract in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocContractId = (cursor) => {
    return "packageDocumentContractId" + cursor
}

/**
 * This function will generate a name for the field of DSDocument Tarification id in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocTarificationId = (cursor) => {
    return "packageDocumentTarificationId" + cursor
}

/**
 * This function will generate a name for the field of DSDocument Tarification name in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocTarificationNumber = (cursor) => {
    return "packageDocumentTarificationNumber" + cursor
}

/**
 * This function will generate a name for the field of DSDocument Claim id in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocClaimId = (cursor) => {
    return "packageDocumentClaimId" + cursor
}

/**
 * This function will generate a name for the field of DSDocument Claim number in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocClaimNumber = (cursor) => {
    return "packageDocumentClaimNumber" + cursor
}

/**
 * This function will generate a name for the field of DSDocument ID in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocId = (cursor) => {
    return "packageDocumentId" + cursor
}

/**
 * This function will generate a name for the field of Origin in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocOrigin = (cursor) => {
    return "packageDocumentOrigin" + cursor
}

/**
 * This function will generate a name for the field of pdf name in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForTemporaryUploadId = (cursor) => {
    return "packageDocumentTemporaryUploadId" + cursor
}

/**
 * This function will generate a name for the field of pdf name in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocumentTypeIdx = (cursor) => {
    return "packageDocumentDocumentTypeIdx" + cursor
}

/**
 * This function will generate upload document index name for local document in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocumentUploadGroupTypeId = (cursor) => {
    return "packageDocumentDocumentUploadGroupTypeId" + cursor
}

/**
 * This function will generate a name for the field of pdf name in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForUploadComment = (cursor) => {
    return "packageDocumentDocumentUploadComment" + cursor
}

/**
 * This function will generate a name for the field of original filename in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocUploadedFileOriginalName = (cursor) => {
    return "packageDocumentUploadedFileOriginalName" + cursor
}

/**
 * This function will generate a name for the field of oracle ID in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocOracleId = (cursor) => {
    return "packageDocumentOracleDocumentId" + cursor
}

/**
 * This function will generate a name for the field of oracle type in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocOracleType = (cursor) => {
    return "packageDocumentOracleDocumentType" + cursor
}

/**
 * This function will generate a name for the field of OracleDocumentUploadDate in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForOracleDocumentUploadDate = (cursor) => {
    return "packageDocumentOracleDate" + cursor
}

/**
 * This function will generate a name for the field of oracle name in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocOracleName = (cursor) => {
    return "packageDocumentOracleName" + cursor
}

/**
 * This function will generate a name for the field of oracle type in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForDocSelectionTab = (cursor) => {
    return "packageDocumentSelectionTab" + cursor
}

/**
 * This function will generate a name for the field of oracle type in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForSize = (cursor) => {
    return "packageDocumentSize" + cursor
}

/**
 * This function will generate a name for the field of oracle type in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameForEncrypted = (cursor) => {
    return "packageDocumentEncrypted" + cursor
}

/**
 * This function will generate a name for the field of oracle type in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameAttachmentConnectionId = (cursor) => {
    return "packageDocumentAttachmentConnectionId" + cursor
}

/**
 * This function will generate a name for the is attachment flag in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNameIsAttachment = (cursor) => {
    return "packageDocumentIsAttachment" + cursor
}

/**
 * This function will generate a name for the position of the attachment file in the merged document in the dynamic document list.
 * @param {number} cursor
 */
export const getDynamicFieldNamePositionInUploadDoc = (cursor) => {
    return "packageDocumentPositionInUploadDoc" + cursor
}

/**
 * Returns with the name of the packageId
 */
export const getPackageIdFieldName = () => {
    return "packageId"
}

/**
 * This function provide form input fields for a single document by a document record
 * @param {*} oneDocument
 */
export const getDynamicInputFieldByCursor = (documentRecord, moduleKey, subModuleKey, DSDocumentOriginTypes) => {
    let initialDocumentFields = {}
    // deep clone to not lose
    let oneDocument = JSON.parse(JSON.stringify(documentRecord))
    let cursor = oneDocument.cursor
    let docIdFieldName = getDynamicFieldNameForDocId(cursor)
    let docOriginFieldName = getDynamicFieldNameForDocOrigin(cursor)
    let docTemplateFieldName = getDynamicFieldNameForDocTemplate(cursor)
    let docLangFieldName = getDynamicFieldNameForDocLang(cursor)
    let docTeamFieldName = getDynamicFieldNameForDocTeam(cursor)
    let docTeamNameFieldName = getDynamicFieldNameForDocTeamName(cursor)
    let docTeamSubgroupFieldName = getDynamicFieldNameForDocTeamSubgroup(cursor)
    let docContractFieldName = getDynamicFieldNameForDocContractNumber(cursor)
    let docContractIdFieldName = getDynamicFieldNameForDocContractId(cursor)
    let docTarificationIdFieldName = getDynamicFieldNameForDocTarificationId(cursor)
    let docTarificationNumberFieldName = getDynamicFieldNameForDocTarificationNumber(cursor)
    let docClaimIdFieldName = getDynamicFieldNameForDocClaimId(cursor)
    let docClaimNumberFieldName = getDynamicFieldNameForDocClaimNumber(cursor)
    // uploaded documents
    let docTemporaryUploadIdFieldName = getDynamicFieldNameForTemporaryUploadId(cursor)
    let docUploadedFileOriginalNameFieldName = getDynamicFieldNameForDocUploadedFileOriginalName(cursor)
    // oracle documents
    let docDocumentTypeIdxFieldName = getDynamicFieldNameForDocumentTypeIdx(cursor)
    let docDocumentUploadGroupTypeIdFieldName = getDynamicFieldNameForDocumentUploadGroupTypeId(cursor)
    let docUploadCommentFieldName = getDynamicFieldNameForUploadComment(cursor)
    let docOracleIdFieldName = getDynamicFieldNameForDocOracleId(cursor)
    let docOracleTypeFieldName = getDynamicFieldNameForDocOracleType(cursor)
    let docOracleName = getDynamicFieldNameForDocOracleName(cursor)
    let oracleDocumentUploadDate = getDynamicFieldNameForOracleDocumentUploadDate(cursor)
    let documentSelectionTabFieldName = getDynamicFieldNameForDocSelectionTab(cursor)
    let attachmentConnectionIdFieldName = getDynamicFieldNameAttachmentConnectionId(cursor)
    let isAttachmentFieldName = getDynamicFieldNameIsAttachment(cursor)
    let positionInUploadDocName = getDynamicFieldNamePositionInUploadDoc(cursor)
    let documentSizeFieldName = getDynamicFieldNameForSize(cursor)
    let documentEncryptedFieldName = getDynamicFieldNameForEncrypted(cursor)

    initialDocumentFields[docIdFieldName] = {
        ...(_initValueOfInput(docIdFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocId"),
        value: oneDocument._id || ""
    }

    //V8.0
    initialDocumentFields[docOriginFieldName] = {
        ...(_initValueOfInput(docOriginFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocOriginFieldName"),
        value: oneDocument.origin || ""
    }

    initialDocumentFields[docTemplateFieldName] = {
        ...(_initValueOfInput(docTemplateFieldName)),
        isRequired: oneDocument.origin == DSDocumentOriginTypes.DS_ORIGIN_GENERATED ? true : false,
        //isRequired: true,
        errorMessage: _("invalidDocType"),
        value: nesProp.get(oneDocument, "documentTemplateId") || null
    }

    initialDocumentFields[docLangFieldName] = {
        ...(_initValueOfInput(docLangFieldName)),
        isRequired: oneDocument.origin == DSDocumentOriginTypes.DS_ORIGIN_GENERATED ? true : false,
        //isRequired: true,
        errorMessage: _("invalidDocLang"),
        value: nesProp.get(oneDocument, "language") || null
    }

    initialDocumentFields[docTeamFieldName] = {
        ...(_initValueOfInput(docTeamFieldName)),
        isRequired: (moduleKey === c.DS_MODULE_CLIENT_TARIFICATION && subModuleKey !== c.DS_MODULE_CLIENT_TARIFICATION),
        requiredErrorMessage: _("requiredDocTeam"),
        errorMessage: _("invalidDocTeam"),
        value: nesProp.get(oneDocument, "team") || null
    }

    initialDocumentFields[docTeamNameFieldName] = {
        ...(_initValueOfInput(docTeamNameFieldName)),
        isRequired: (moduleKey === c.DS_MODULE_CLIENT_TARIFICATION && subModuleKey !== c.DS_MODULE_CLIENT_TARIFICATION),
        errorMessage: _("invalidDocTeamName"),
        value: nesProp.get(oneDocument, "teamName") || null
    }

    initialDocumentFields[docContractFieldName] = {
        ...(_initValueOfInput(docContractFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocContract"),
        value: nesProp.get(oneDocument, "contractNumber") || ""
    }

    initialDocumentFields[docContractIdFieldName] = {
        ...(_initValueOfInput(docContractIdFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocContractId"),
        value: nesProp.get(oneDocument, "contractId") || ""
    }

    initialDocumentFields[docTarificationIdFieldName] = {
        ...(_initValueOfInput(docTarificationIdFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocTarificationId"),
        value: nesProp.get(oneDocument, "tarificationId") || ""
    }

    initialDocumentFields[docTarificationNumberFieldName] = {
        ...(_initValueOfInput(docTarificationNumberFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocTarificationNumber"),
        value: nesProp.get(oneDocument, "tarificationNumber") || ""
    }

    initialDocumentFields[docClaimIdFieldName] = {
        ...(_initValueOfInput(docClaimIdFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocClaimId"),
        value: nesProp.get(oneDocument, "claimId") || ""
    }

    initialDocumentFields[docClaimNumberFieldName] = {
        ...(_initValueOfInput(docClaimNumberFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocClaimNumber"),
        value: nesProp.get(oneDocument, "claimNumber") || ""
    }

    initialDocumentFields[docTeamSubgroupFieldName] = {
        ...(_initValueOfInput(docTeamSubgroupFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocTeamSubgroup"),
        value: nesProp.get(oneDocument, "teamSubgroup") || null
    }

    // V8.0 - local uploaded documents related fields
    initialDocumentFields[docTemporaryUploadIdFieldName] = {
        ...(_initValueOfInput(docTemporaryUploadIdFieldName)),
        isRequired: false,
        errorMessage: _("invalidTemporaryUploadId"),
        value: nesProp.get(oneDocument, "temporaryUploadId") || ""
    }

    initialDocumentFields[docDocumentTypeIdxFieldName] = {
        ...(_initValueOfInput(docDocumentTypeIdxFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocumentTypeIdxFieldName"),
        value: nesProp.get(oneDocument, "documentTypeIdx") || ""
    }

    initialDocumentFields[docDocumentUploadGroupTypeIdFieldName] = {
        ...(_initValueOfInput(docDocumentUploadGroupTypeIdFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocumentUploadGroupTypeIdName"),
        value: nesProp.get(oneDocument, "documentUploadGroupTypeId") || ""
    }

    initialDocumentFields[docUploadCommentFieldName] = {
        ...(_initValueOfInput(docUploadCommentFieldName)),
        isRequired: false,
        errorMessage: _("invalidUploadComment"),
        value: nesProp.get(oneDocument, "uploadComment") || ""
    }

    initialDocumentFields[docUploadedFileOriginalNameFieldName] = {
        ...(_initValueOfInput(docUploadedFileOriginalNameFieldName)),
        isRequired: false,
        errorMessage: _("invalidUploadedocOracleIdFieldNamedFileOriginalName"),
        value: nesProp.get(oneDocument, "originalFileName") || ""
    }

    // v8.0 oracle documents related fields
    initialDocumentFields[docOracleIdFieldName] = {
        ...(_initValueOfInput(docOracleIdFieldName)),
        isRequired: false,
        errorMessage: _("invalidOracleId"),
        value: nesProp.get(oneDocument, "oracleDocumentId") || ""
    }

    initialDocumentFields[docOracleTypeFieldName] = {
        ...(_initValueOfInput(docOracleTypeFieldName)),
        isRequired: false,
        errorMessage: _("invalidOracleType"),
        value: nesProp.get(oneDocument, "oracleDocumentType") || ""
    }

    initialDocumentFields[docOracleName] = {
        ...(_initValueOfInput(docOracleName)),
        isRequired: false,
        errorMessage: _("invalidOracleDocumentName"),
        value: nesProp.get(oneDocument, "oracleDocumentName") || ""
    }

    initialDocumentFields[oracleDocumentUploadDate] = {
        ...(_initValueOfInput(oracleDocumentUploadDate)),
        isRequired: false,
        errorMessage: _("invalidOracleDocumentUploadDate"),
        value: nesProp.get(oneDocument, "oracleDocumentUploadDate") || ""
    }

    initialDocumentFields[documentSelectionTabFieldName] = {
        ...(_initValueOfInput(documentSelectionTabFieldName)),
        isRequired: false,
        errorMessage: _("invalidDoscumentSelectionTab"),
        value: nesProp.get(oneDocument, 'documentSelectionTab')
    }

    initialDocumentFields[attachmentConnectionIdFieldName] = {
        ...(_initValueOfInput(attachmentConnectionIdFieldName)),
        isRequired: false,
        errorMessage: _("invalidAttachmentConnectionId"),
        value: nesProp.get(oneDocument, 'attachmentConnectionId')
    }

    initialDocumentFields[isAttachmentFieldName] = {
        ...(_initValueOfInput(isAttachmentFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocumentIsAttachment"),
        value: nesProp.get(oneDocument, 'isAttachment')
    }

    initialDocumentFields[positionInUploadDocName] = {
        ...(_initValueOfInput(positionInUploadDocName)),
        isRequired: false,
        errorMessage: _("invalidDocumentPositionInUploadDoc"),
        value: nesProp.get(oneDocument, 'positionInUploadDoc')
    }

    initialDocumentFields[documentSizeFieldName] = {
        ...(_initValueOfInput(documentSizeFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocumentSize"),
        isValid: (size) => ((Number(size) <= 30 * (1024 ** 2))),
        value: Number(nesProp.get(oneDocument, 'fileSize'))
    }

    initialDocumentFields[documentEncryptedFieldName] = {
        ...(_initValueOfInput(documentEncryptedFieldName)),
        isRequired: false,
        errorMessage: _("invalidDocumentEncryption"),
        isValid: (isEncrypted) => {
            isEncrypted = (isEncrypted === "false" || !isEncrypted) ? false : true
            return (!isEncrypted)
        },
        value: (nesProp.get(oneDocument, 'isEncrypted') === "false" || !nesProp.get(oneDocument, 'isEncrypted')) ? false : true
    }

    return initialDocumentFields
}


/**
 * This function will return a list with form field values for documents.
 * @param {*} packageDSDocuments
 * @returns
 */
export const getFlatValueListOfDsDocumentsForForm = (packageDSDocuments, documentTemplateOptions) => {
    // if there is many options we let it blank, no default selection yet
    //let contractNumbersOptions = this.getContractNumbersInOptionList()
    let flatDocumentsValueList = {}

    if (packageDSDocuments) {
        let cursor = 0

        packageDSDocuments.forEach((oneDocument) => {
            cursor = oneDocument.cursor

            let docIdFieldName = getDynamicFieldNameForDocId(cursor)
            let docOriginFieldName = getDynamicFieldNameForDocOrigin(cursor)
            let docTemplateFieldName = getDynamicFieldNameForDocTemplate(cursor)
            let docLangFieldName = getDynamicFieldNameForDocLang(cursor)
            let docTeamFieldName = getDynamicFieldNameForDocTeam(cursor)
            let docTeamNameFieldName = getDynamicFieldNameForDocTeamName(cursor)
            let docTeamSubgroupFieldName = getDynamicFieldNameForDocTeamSubgroup(cursor)
            let docContractFieldName = getDynamicFieldNameForDocContractNumber(cursor)
            let docContractIdFieldName = getDynamicFieldNameForDocContractId(cursor)
            let docTarificationIdFieldName = getDynamicFieldNameForDocTarificationId(cursor)
            let docTarificationNumberFieldName = getDynamicFieldNameForDocTarificationNumber(cursor)
            let docClaimIdFieldName = getDynamicFieldNameForDocClaimId(cursor)
            let docClaimNumberFieldName = getDynamicFieldNameForDocClaimNumber(cursor)
            // uploaded document related
            let docTemporaryUploadIdFieldName = getDynamicFieldNameForTemporaryUploadId(cursor)
            let docUploadedFileOriginalNameFieldName = getDynamicFieldNameForDocUploadedFileOriginalName(cursor)
            let docDocumentTypeIdxFieldName = getDynamicFieldNameForDocumentTypeIdx(cursor)
            let docDocumentUploadGroupTypeIdFieldName = getDynamicFieldNameForDocumentUploadGroupTypeId(cursor)
            let docUploadCommentFieldName = getDynamicFieldNameForUploadComment(cursor)

            // oracle document related
            let docOracleIdFieldName = getDynamicFieldNameForDocOracleId(cursor)
            let docOracleTypeFieldName = getDynamicFieldNameForDocOracleType(cursor)
            let docOracleName = getDynamicFieldNameForDocOracleName(cursor)
            let oracleDocumentUploadDate = getDynamicFieldNameForOracleDocumentUploadDate(cursor)
            let documentSelectionTabFieldName = getDynamicFieldNameForDocSelectionTab(cursor)
            let attachmentConnectionIdFieldName = getDynamicFieldNameAttachmentConnectionId(cursor)
            let isAttachmentFieldName = getDynamicFieldNameIsAttachment(cursor)
            let positionInUploadDocName = getDynamicFieldNamePositionInUploadDoc(cursor)
            let documentSizeFieldName = getDynamicFieldNameForSize(cursor)
            let documentEncryptedFieldName = getDynamicFieldNameForEncrypted(cursor)


            flatDocumentsValueList[docIdFieldName] = oneDocument._id || ""
            flatDocumentsValueList[docOriginFieldName] = oneDocument.origin || ""
            flatDocumentsValueList[docTemplateFieldName] = documentTemplateOptions && documentTemplateOptions.length &&
                (nesProp.get(oneDocument, "documentTemplateId") || "") || ""
            flatDocumentsValueList[docLangFieldName] = oneDocument.language
            flatDocumentsValueList[docTeamFieldName] = oneDocument.team || ""
            flatDocumentsValueList[docTeamNameFieldName] = oneDocument.teamName || ""
            flatDocumentsValueList[docContractFieldName] = oneDocument.contractNumber || ""
            flatDocumentsValueList[docContractIdFieldName] = oneDocument.contractId || ""
            flatDocumentsValueList[docTarificationIdFieldName] = oneDocument.tarificationId || ""
            flatDocumentsValueList[docTarificationNumberFieldName] = oneDocument.tarificationNumber || ""
            flatDocumentsValueList[docClaimIdFieldName] = oneDocument.claimId || ""
            flatDocumentsValueList[docClaimNumberFieldName] = oneDocument.claimNumber || ""
            flatDocumentsValueList[docTeamSubgroupFieldName] = oneDocument.teamSubgroup || ""
            // uploaded documents
            flatDocumentsValueList[docTemporaryUploadIdFieldName] = oneDocument.temporaryUploadId || ""
            flatDocumentsValueList[docUploadedFileOriginalNameFieldName] = oneDocument.originalFileName || ""
            flatDocumentsValueList[docDocumentTypeIdxFieldName] = oneDocument.documentTypeIdx || ""
            flatDocumentsValueList[docDocumentUploadGroupTypeIdFieldName] = oneDocument.documentUploadGroupTypeId || ""
            flatDocumentsValueList[docUploadCommentFieldName] = oneDocument.uploadComment || ""
            // oracle related documents
            flatDocumentsValueList[docOracleIdFieldName] = oneDocument.oracleDocumentId || ""
            flatDocumentsValueList[docOracleTypeFieldName] = oneDocument.oracleDocumentType || ""
            flatDocumentsValueList[docOracleName] = oneDocument.oracleDocumentName || ""
            flatDocumentsValueList[oracleDocumentUploadDate] = oneDocument.oracleDocumentUploadDate || ""
            flatDocumentsValueList[documentSelectionTabFieldName] = oneDocument.documentSelectionTab || ""
            flatDocumentsValueList[attachmentConnectionIdFieldName] = oneDocument.attachmentConnectionId || ""
            flatDocumentsValueList[isAttachmentFieldName] = oneDocument.isAttachment || false
            flatDocumentsValueList[positionInUploadDocName] = oneDocument.positionInUploadDoc || ''
            flatDocumentsValueList[documentSizeFieldName] = Number(oneDocument.fileSize) || 0
            flatDocumentsValueList[documentEncryptedFieldName] = (!oneDocument.isEncrypted || oneDocument.isEncrypted === "false") ? false : true
        })
    }

    return flatDocumentsValueList
}

/**
 * This function will remove values from signing fields in case documents that require signing are removed from docs list
 * @param {*} formInputs
 * @returns
 */
export const removeSigningInputFieldsValues = (formInputs) => {
    const signingFields = ['clientEmailAddress', 'brokerEmailAddress', 'remainderInterval',
        'remainderRepeats', 'firstSignerFirstName', 'firstSignerLastName', 'firstSignerPhoneNumber',
        'firstSignerCountryPhoneCode']

    for (const field of signingFields) {
        formInputs[field].isRequired = false
        formInputs[field].value = ''
        formInputs[field].touched = false
    }

    return formInputs
}


/**
 * This function wil set default values for first signer fields, reminder fields and broker email address
 * @param {*} formInputs
 * @param {*} packageDSDocuments
 * @returns
 */
export const setSigningInputFields = (formInputs, formInitialValues, requireSmsOtpSigning) => {
    const signingFields = ['clientEmailAddress', 'brokerEmailAddress', 'remainderInterval',
        'remainderRepeats', 'firstSignerFirstName', 'firstSignerLastName', 'remainderTicketInterval',
        'remainderTicketRepeats']

    if (requireSmsOtpSigning) {
        signingFields.push('firstSignerPhoneNumber', 'firstSignerCountryPhoneCode')
    } else {
        formInputs['firstSignerPhoneNumber'].isRequired = false
        formInputs['firstSignerPhoneNumber'].value = ''
        formInputs['firstSignerPhoneNumber'].touched = false
        formInputs['firstSignerCountryPhoneCode'].isRequired = false
        formInputs['firstSignerCountryPhoneCode'].value = ''
        formInputs['firstSignerCountryPhoneCode'].touched = false
    }

    for (const field of signingFields) {
        if (!formInputs[field].touched) {
            formInputs[field].isRequired = true
            formInputs[field].value = nesProp.get(formInitialValues, field)
            formInputs[field].touched = false
        }
    }

    return formInputs
}

/**
* Fill the form values ins state
* @param {*} data
* @param {*} formInputs
*
* @returns formInputs - reference to formInputs object in state
*/
export const setFormInputsByFormValues = (data, formInputs) => {
    if (!formInputs) {
        return formInputs
    }

    const formInputNames = Object.keys(formInputs)
    formInputNames.forEach((formFieldName) => {

        let dataField = nesProp.get(data, formFieldName)
        let formField = nesProp.get(formInputs, formFieldName)

        formField.value = dataField

        // set back the value but this is already set by address, but must implement to see what happen
        // so if you delete this row don't prevent tha value change caused by field Validation. (Only the deep cloning of values can solve.)
        formInputs[formFieldName] = formField
    })

    return formInputs
}

export const DS_WIZARD_ERROR_CODE_GENERAL = 'DS_WIZARD_ERROR_CODE_GENERAL'
export const DS_WIZARD_ERROR_CODE_NO_DOCUMENTS = 'DS_WIZARD_ERROR_CODE_NO_DOCUMENTS'
export const DS_WIZARD_ERROR_CODE_TO_MUCH_DOCUMENTS = 'DS_WIZARD_ERROR_CODE_TO_MUCH_DOCUMENTS'
export const DS_WIZARD_ERROR_CODE_NO_GENERATED_DOC = 'DS_WIZARD_ERROR_CODE_NO_GENERATED_DOC'

/**
 * Return true if the form is valid else false.
 * @param {*} formInputs
 * @param {*} packageDSDocuments
 * @param {*} DSDocumentOriginTypes
 * @param {*} translations
 * @returns Boolean or error messages
 */
export const validateTheForm = (formInputs, packageDSDocuments, DSDocumentOriginTypes, forceTouche = false) => {
    // every change must set the valid form
    if (!formInputs) {
        return false
    }

    let hasError = []
    let keys = Object.keys(formInputs) || []

    // todo improve the form validity check contirioning if client is company to skip clientFirstName
    keys.forEach((oneKey) => {
        if (formInputs[oneKey].error) {
            // this if is made only for debug.
            if (formInputs[oneKey].touched) {
                console.warn("Form input '", oneKey, "' error: ", formInputs[oneKey].error)
            }

            if (!hasError.some(item => (item.code === DS_WIZARD_ERROR_CODE_GENERAL))) {
                hasError.push({
                    message: _('formInputsAreNotValid'),
                    code: DS_WIZARD_ERROR_CODE_GENERAL,
                    touched: forceTouche
                })
            }
        }
    })

    // must has minimum one document for the package
    if (!packageDSDocuments || packageDSDocuments.length === 0) {
        console.warn("No package documents found.")

        if (!hasError.some(item => (item.code === DS_WIZARD_ERROR_CODE_NO_DOCUMENTS))) {
            hasError.push({
                message: _('noDocumentsFoundInPackage'),
                code: DS_WIZARD_ERROR_CODE_NO_DOCUMENTS,
                touched: forceTouche
            })
        }
    }

    if (packageDSDocuments && packageDSDocuments.length > 15) {
        console.warn("To much file added to the package. Max 15")

        if (!hasError.some(item => (item.code === DS_WIZARD_ERROR_CODE_TO_MUCH_DOCUMENTS))) {
            hasError.push({
                message: _('toManyDocumentsAddedToThePackage'),
                code: DS_WIZARD_ERROR_CODE_TO_MUCH_DOCUMENTS,
                touched: forceTouche
            })
        }
    }

    let totalSize = (packageDSDocuments.reduce((accumulator, currentValue) => (Number(accumulator) + Number(currentValue.fileSize)), 0) / 1024) / 1024

    if (totalSize > 150) {
        console.warn("Pacakge size exceeded  bigger than 150 MB")

        if (!hasError.some(item => (item.code === DS_WIZARD_ERROR_CODE_NO_DOCUMENTS))) {
            hasError.push({
                message: _('theTotalSizeOfFilesIsMoreThanMax'),
                code: DS_WIZARD_ERROR_CODE_NO_DOCUMENTS,
                touched: forceTouche
            })
        }
    }

    let isValidTheForm = {
        isValid: hasError.length === 0 ? true : false,
        errors: hasError
    }

    return isValidTheForm
}


/**
 * This function validate one field from the form.
 * @param {*} formField - instance of input field object
 * @param {*} forceTouch - touch anyway the input even if the value is not modified (useful on onBluer fired or onSubmit)
 */
export const fieldValidation = (formField, forceTouch = false) => {
    // required check
    if (formField.isRequired && !formField.value) {
        formField.error = formField.errorMessageRequired || _("fieldRequired")
        formField.touched = forceTouch ? true : formField.touched
    } else {
        // validate if "isValid" property is set
        if (formField.isValid && formField.value) {
            if (formField.isValid(formField.value)) {
                formField.error = false
            } else {
                formField.error = formField.errorMessage
                formField.touched = forceTouch ? true : formField.touched
            }
        } else {
            formField.error = false
            formField.touched = forceTouch ? true : formField.touched
        }
    }

    return formField
}

/**
 * Validate the hole form.
 * @param {*} formInputs
 * @param {*} forceTouch - touch anyway the input even if the value is not modified (useful on onBluer fired or onSubmit)
 * @returns formInputs - reference to formInputs object in state
 */
export const formInputsValidation = (formInputs, forceTouch = false) => {
    if (!formInputs) {
        return formInputs
    }

    for (const currentFormFieldName in formInputs) {
        let currentFormFiled = formInputs[currentFormFieldName]

        // THIS IF MADE FOR EMAIL SERVER CONFIG for email address
        // if (!(currentFormFieldName !== 'brokerEmailAddress' ||
        //     (currentFormFieldName === 'brokerEmailAddress' && !currentFormFiled.error))) {
        //         // following line make no sense, get out an d put back? Why?
        //         formInputs[currentFormFieldName] = currentFormFiled
        //     return
        // }

        currentFormFiled = fieldValidation(currentFormFiled, forceTouch)

        // set back the value but this is already set by address, but must implement to see what happen
        // so if you delete this row don't prevent tha value change caused by field Validation. (Only the deep cloning of values can solve.)
        formInputs[currentFormFieldName] = currentFormFiled
    }

    // reset the flag of form validity and return with the form inputs
    return formInputs
}

/**
 * IMPORTANT -> do not call this function separately, used in remove-DSDocumentFromThePackage only
 * Update the form field inputs list also to be in synchronised with the documents list
 * let docTemplateFieldName = this.getDynamicFieldNameForDocTemplate(cursor)
 * @param {*} cursor
 * @param {*} formInputs
 * @param {*} packageDSDocuments
 * @param {*} DSDocumentOriginTypes
 * @returns
 */
export const removeFormInputByCursor = (cursor, formInputs) => {
    let docIdFieldName = getDynamicFieldNameForDocId(cursor)
    let docOriginFieldName = getDynamicFieldNameForDocOrigin(cursor)
    let docTemplateFieldName = getDynamicFieldNameForDocTemplate(cursor)
    let docLangFieldName = getDynamicFieldNameForDocLang(cursor)
    let docTeamFieldName = getDynamicFieldNameForDocTeam(cursor)
    let docTeamNameFieldName = getDynamicFieldNameForDocTeamName(cursor)
    let docTeamSubgroupFieldName = getDynamicFieldNameForDocTeamSubgroup(cursor)
    let docContractFieldName = getDynamicFieldNameForDocContractNumber(cursor)
    let docContractIdFieldName = getDynamicFieldNameForDocContractId(cursor)
    let docTarificationIdFieldName = getDynamicFieldNameForDocTarificationId(cursor)
    let docTarificationNumberFieldName = getDynamicFieldNameForDocTarificationNumber(cursor)
    let docClaimIdFieldName = getDynamicFieldNameForDocClaimId(cursor)
    let docClaimNumberFieldName = getDynamicFieldNameForDocClaimNumber(cursor)
    // uploaded documents
    let docTemporaryUploadIdFieldName = getDynamicFieldNameForTemporaryUploadId(cursor)
    let docUploadedFileOriginalNameFieldName = getDynamicFieldNameForDocUploadedFileOriginalName(cursor)
    let docDocumentTypeIdxFieldName = getDynamicFieldNameForDocumentTypeIdx(cursor)
    let docDocumentUploadGroupTypeIdFieldName = getDynamicFieldNameForDocumentUploadGroupTypeId(cursor)
    let docUploadCommentFieldName = getDynamicFieldNameForUploadComment(cursor)
    let attachmentConnectionIdFieldName = getDynamicFieldNameAttachmentConnectionId(cursor)
    let isAttachmentFieldName = getDynamicFieldNameIsAttachment(cursor)
    let positionInUploadDocName = getDynamicFieldNamePositionInUploadDoc(cursor)

    // oracle selected
    let docOracleIdFieldName = getDynamicFieldNameForDocOracleId(cursor)
    let docOracleTypeFieldName = getDynamicFieldNameForDocOracleType(cursor)
    let docOracleName = getDynamicFieldNameForDocOracleName(cursor)
    let oracleDocumentUploadDate = getDynamicFieldNameForOracleDocumentUploadDate(cursor)
    let documentSizeFieldName = getDynamicFieldNameForSize(cursor)
    let documentEncryptedFieldName = getDynamicFieldNameForEncrypted(cursor)

    delete formInputs[docIdFieldName]
    delete formInputs[docOriginFieldName]
    delete formInputs[docTemplateFieldName]
    delete formInputs[docLangFieldName]
    delete formInputs[docTeamFieldName]
    delete formInputs[docTeamNameFieldName]
    delete formInputs[docContractFieldName]
    delete formInputs[docContractIdFieldName]
    delete formInputs[docTarificationIdFieldName]
    delete formInputs[docTarificationNumberFieldName]
    delete formInputs[docClaimIdFieldName]
    delete formInputs[docClaimNumberFieldName]
    delete formInputs[docTeamSubgroupFieldName]
    // uploaded documents
    delete formInputs[docTemporaryUploadIdFieldName]
    delete formInputs[docUploadedFileOriginalNameFieldName]
    delete formInputs[docDocumentTypeIdxFieldName]
    delete formInputs[docDocumentUploadGroupTypeIdFieldName]
    delete formInputs[docUploadCommentFieldName]
    delete formInputs[attachmentConnectionIdFieldName]
    delete formInputs[isAttachmentFieldName]
    delete formInputs[positionInUploadDocName]
    // oracle documents
    delete formInputs[docOracleIdFieldName]
    delete formInputs[docOracleTypeFieldName]
    delete formInputs[docOracleName]
    delete formInputs[oracleDocumentUploadDate]
    delete formInputs[documentSizeFieldName]
    delete formInputs[documentEncryptedFieldName]

    return formInputs
}

/**
 * Fetch values from form by input refs.
 * @param {*} formInputs
 * @param {*} refs
 * @param {*} packageDSDocuments
 * @param {*} moduleKey
 * @param {*} subModuleKey
 * @returns
 */
export const fetchFormValues = (formInputs, refs, packageDSDocuments, moduleKey, subModuleKey) => {
    let formData = {}
    // first check the physical inputs for values
    for (const formFieldName in formInputs) {
        //get value from form state 
        let formFieldValue = nesProp.get(formInputs, formFieldName + ".value")

        // if there is no value in state than try to get from refs as to not brake old functionality 
        //react creatable select way to get value
        formFieldValue = formFieldValue || nesProp.get(refs, formFieldName + ".props.value.value")
        // if not then react select way to get value
        formFieldValue = formFieldValue || nesProp.get(refs, formFieldName + ".state.value.value")

        // if not then normal text input way to get value
        formFieldValue = formFieldValue || nesProp.get(refs, formFieldName + ".value")

        formData[formFieldName] = formFieldValue
    }

    // overwrite the non existent physical inputs, because there are form values what must be passed forward, but does not have real inputs
    // this values are not shown in the form, because are not editable only must pass forward fot create package
    formData["expirationOn"] = nesProp.get(formInputs, "expirationOn.value")
    formData["clientFirstName"] = nesProp.get(formInputs, "clientFirstName.value")
    formData["clientLastName"] = nesProp.get(formInputs, "clientLastName.value")
    formData["clientBirthDate"] = nesProp.get(formInputs, "clientBirthDate.value")
    formData["clientLanguageWord"] = nesProp.get(formInputs, "clientLanguageWord.value")
    formData["status"] = nesProp.get(formInputs, "status.value")
    formData["brokerEmailAddress"] = nesProp.get(formInputs, "brokerEmailAddress.value")
    formData["brokerName"] = nesProp.get(formInputs, "brokerName.value")
    formData["contractId"] = nesProp.get(formInputs, "contractId.value")
    formData["contractNumber"] = nesProp.get(formInputs, "contractNumber.value")
    formData["claimId"] = nesProp.get(formInputs, "claimId.value")
    formData["claimNumber"] = nesProp.get(formInputs, "claimNumber.value")
    formData["team"] = nesProp.get(formInputs, "team.value")
    formData["teamName"] = nesProp.get(formInputs, "teamName.value")
    formData["teamSubgroup"] = nesProp.get(formInputs, "teamSubgroup.value")
    formData["tarificationId"] = nesProp.get(formInputs, "tarificationId.value")
    formData["tarificationNumber"] = nesProp.get(formInputs, "tarificationNumber.value")

    // Language translated to code, don't know if this word must used,
    // !!! TODO !!! check it for what was used th LanguageWord
    // const clientOptionsDict = this.state.clientOptionsDict || this.props.clientOptionsDict
    // let clientLanguage = ''
    // if (clientOptionsDict) {
    //     clientLanguage = getTheLanguageCodeByAnyLanguageOnAnyLanguage(formData["clientLanguageWord"], clientOptionsDict)
    // }

    formData["clientLanguage"] = nesProp.get(formInputs, "clientLanguage.value")
    // not a real input in form so must generate the value in a non natural way when fetching the form values.
    formData["hasObligatedTarificationTemplate"] = isATarificationAndHasAMarkedTemplate(moduleKey, packageDSDocuments, subModuleKey)
    formData["hasObligatedMandateTemplate"] = isMandateAndHasAMarkedTemplate(moduleKey, packageDSDocuments, subModuleKey)

    return formData
}


/**
 * This function will generate the list of input fields for state.
 * This also will keep the values from the documents
 * @param {*} packageDSDocuments
 * @param {*} moduleKey
 * @param {*} subModuleKey
 * @param {*} DSDocumentOriginTypes
 * @returns
 */
export const getFormDynamicInputFieldsList = (packageDSDocuments, moduleKey, subModuleKey, DSDocumentOriginTypes) => {
    let initialDocumentFields = {}

    if (packageDSDocuments) {
        packageDSDocuments.forEach((oneDocument) => {
            let newField = getDynamicInputFieldByCursor(oneDocument, moduleKey, subModuleKey, DSDocumentOriginTypes)

            // add field
            initialDocumentFields = {
                ...initialDocumentFields,
                ...newField
            }
        })
    }

    return initialDocumentFields
}


/**
 * Compose and setup the form inputs with validation an custom error messages
 * For ex: tarification needs a special template anyway to continue the wizard.
 * @param {*} formInitialValues - initial values for the form.
 * @param {*} packageDSDocuments
 * @param {*} DSDocumentOriginTypes
 * @param {*} moduleKey - module key for init some rules for the form depending on modules key.
 * @param {*} subModuleKey
 * @param {*} isSignable
 * @param {*} stakeholders
 * @param {*} requireSmsOtpSigning
 * @returns
 */
export const createNewFormInputFieldsList = (formInitialValues, packageDSDocuments, DSDocumentOriginTypes, moduleKey, subModuleKey,
    isSignable = false, stakeholders, requireSmsOtpSigning, isOffice) => {

    // the initial values are in state in "packageDSDocuments"
    let dynamicInputFieldList = getFormDynamicInputFieldsList(packageDSDocuments, moduleKey, subModuleKey, DSDocumentOriginTypes)

    let packageIdFieldName = getPackageIdFieldName()
    let clientTypeCode = nesProp.get(formInitialValues, "clientTypeCode")
    const firstSigningStakeholder = stakeholders && stakeholders.find(stakeholder => stakeholder.signerNumber === '0')
    const secondSigningStakeholder = stakeholders && stakeholders.find(stakeholder => stakeholder.signerNumber === '1')
    const brokerSigningStakeholder = stakeholders && stakeholders.find(stakeholder => stakeholder.signerType === "BROKER")

    const brokerSignerPhoneNumber = brokerSigningStakeholder ?
        spreadPhoneNumber(brokerSigningStakeholder.phoneNumber).phoneNumber :
        spreadPhoneNumber(nesProp.get(formInitialValues, "brokerSignerPhoneNumber")).phoneNumber

    const brokerSignerCountryPhoneCode = brokerSigningStakeholder ?
        spreadPhoneNumber(brokerSigningStakeholder.phoneNumber).phoneCode :
        spreadPhoneNumber(nesProp.get(formInitialValues, "brokerSignerCountryPhoneCode")).phoneCode

    return {
        [packageIdFieldName]: {
            ...(_initValueOfInput(packageIdFieldName)),
            isRequired: false,
            value: nesProp.get(formInitialValues, packageIdFieldName)
        },
        "clientEmailAddress": {
            ...(_initValueOfInput("clientEmailAddress")),
            isRequired: isSignable && !isOffice,
            isValid: (value) => (value && isAnEmail(value)),
            errorMessage: _("invalidEmailAddress"),
            value: isSignable ? (firstSigningStakeholder ? firstSigningStakeholder.email : nesProp.get(formInitialValues, 'clientEmailAddress')) : ''
        },
        "clientName": {
            ...(_initValueOfInput("clientName")),
            isRequired: true && !isOffice,
            errorMessage: ((clientTypeCode === c.CLIENT_TYPE_PERSON) ? _("invalidName") : _("invalidCompanyName")),
            value: nesProp.get(formInitialValues, "clientName")
        },
        "contractNumber": {
            ...(_initValueOfInput("contractNumber")),
            isRequired: false,
            errorMessage: _("invalidContractNumber"),
            value: nesProp.get(formInitialValues, "contractNumber")
        },
        "brokerNumber": {
            ...(_initValueOfInput("brokerNumber")),
            isRequired: true,
            errorMessage: _("invalidBrokerNumber"),
            value: nesProp.get(formInitialValues, "brokerNumber")
        },
        "brokerEmailAddress": {
            ...(_initValueOfInput("brokerEmailAddress")),
            isRequired: isSignable,
            errorMessage: _("invalidBrokerEmailAddress"),
            isValid: (value) => (value && isAnEmail(value)),
            value: isSignable ? nesProp.get(formInitialValues, 'brokerEmailAddress') : ''
        },
        "brokerName": {
            ...(_initValueOfInput("brokerName")),
            isRequired: true,
            errorMessage: _("invalidBrokerName"),
            value: nesProp.get(formInitialValues, "brokerName")
        },
        "team": {
            ...(_initValueOfInput("team")),
            isRequired: (moduleKey === c.DS_MODULE_CLIENT_TARIFICATION && subModuleKey !== c.DS_MODULE_CLIENT_TARIFICATION),
            errorMessage: _("invalidTeam"),
            value: nesProp.get(formInitialValues, "team")
        },
        "teamName": {
            ...(_initValueOfInput("teamName")),
            isRequired: false,
            errorMessage: _("invalidTeamName"),
            value: nesProp.get(formInitialValues, "teamName")
        },
        "teamSubgroup": {
            ...(_initValueOfInput("teamSubgroup")),
            isRequired: false,
            errorMessage: _("invalidTeamSubgroup"),
            value: nesProp.get(formInitialValues, "teamSubgroup")
        },
        "tarificationId": {
            ...(_initValueOfInput("tarificationId")),
            isRequired: false,
            errorMessage: _("invalidTarificationId"),
            value: nesProp.get(formInitialValues, "tarificationId")
        },
        "tarificationNumber": {
            ...(_initValueOfInput("tarificationNumber")),
            isRequired: false,
            errorMessage: _("invalidTarificationNumber"),
            value: nesProp.get(formInitialValues, "tarificationNumber")
        },
        "claimId": {
            ...(_initValueOfInput("claimId")),
            isRequired: false,
            errorMessage: _("invalidClaimId"),
            value: nesProp.get(formInitialValues, "claimId")
        },
        "claimNumber": {
            ...(_initValueOfInput("claimNumber")),
            isRequired: false,
            errorMessage: _("invalidClaimNumber"),
            value: nesProp.get(formInitialValues, "claimNumber")
        },
        "remainderInterval": {
            ...(_initValueOfInput("remainderInterval")),
            isRequired: isSignable,
            errorMessage: _("invalidRemainderInterval"),
            value: isSignable ? nesProp.get(formInitialValues, 'remainderInterval') : ''
        },
        "remainderRepeats": {
            ...(_initValueOfInput("remainderRepeats")),
            isRequired: isSignable,
            errorMessage: _("invalidRemainderRepeats"),
            value: isSignable ? nesProp.get(formInitialValues, 'remainderRepeats') : ''
        },
        "remainderTicketInterval": {
            ...(_initValueOfInput("remainderTicketInterval")),
            isRequired: isSignable,
            errorMessage: _("invalidRemainderTicketInterval"),
            value: isSignable ? nesProp.get(formInitialValues, 'remainderTicketInterval') : ''
        },
        "remainderTicketRepeats": {
            ...(_initValueOfInput("remainderTicketRepeats")),
            isRequired: isSignable,
            errorMessage: _("invalidRemainderTicketRepeats"),
            value: isSignable ? nesProp.get(formInitialValues, 'remainderTicketRepeats') : ''
        },
        "expirationOn": {
            ...(_initValueOfInput("expirationOn")),
            isRequired: true,
            errorMessage: _("invalidExpirationOn"),
            isValid: (value) => (value && moment(value).isValid()),
            value: nesProp.get(formInitialValues, "expirationOn")
        },

        "clientFirstName": {
            ...(_initValueOfInput("clientFirstName")),
            isRequired: ((clientTypeCode === c.CLIENT_TYPE_PERSON && !isOffice) ? true : false),
            errorMessage: _("invalidClientFirstName"),
            value: nesProp.get(formInitialValues, "clientFirstName")
        },
        "clientLastName": {
            ...(_initValueOfInput("clientLastName")),
            isRequired: ((clientTypeCode === c.CLIENT_TYPE_PERSON && !isOffice) ? true : false),
            errorMessage: _("invalidClientLastName"),
            value: nesProp.get(formInitialValues, "clientLastName")
        },
        "clientBirthDate": {
            ...(_initValueOfInput("clientBirthDate")),
            isRequired: false,
            errorMessage: _("invalidClientBirthDate"),
            value: nesProp.get(formInitialValues, "clientBirthDate")
        },
        "clientLanguageWord": {
            ...(_initValueOfInput("clientLanguageWord")),
            isRequired: false,
            errorMessage: _("invalidClientLanguageWord"),
            value: nesProp.get(formInitialValues, "clientLanguageWord")
        },
        "clientLanguage": {
            ...(_initValueOfInput("clientLanguage")),
            isRequired: (!isOffice ? true : false),
            errorMessage: _("invalidClientLanguage"),
            value: firstSigningStakeholder ? firstSigningStakeholder.language : nesProp.get(formInitialValues, "clientLanguage")
        },
        "status": {
            ...(_initValueOfInput("status")),
            isRequired: true,
            errorMessage: _("invalidStatus"),
            value: nesProp.get(formInitialValues, "status")
        },
        "hasObligatedTarificationTemplate": {
            ...(_initValueOfInput("hasObligatedTarificationTemplate")),
            isRequired: (moduleKey === c.DS_MODULE_CLIENT_TARIFICATION && subModuleKey !== c.DS_MODULE_CLIENT_TARIFICATION),
            errorMessage: _("noObligatedTarificationTemplateSelected"),
            errorMessageRequired: _("noObligatedTarificationTemplateSelected"),
            isValid: (value) => !((moduleKey == c.DS_MODULE_CLIENT_TARIFICATION && subModuleKey !== c.DS_MODULE_CLIENT_TARIFICATION) && !value),
            //value: nesProp.get(formInitialValues, "hasObligatedTarificationTemplate")
            value: isATarificationAndHasAMarkedTemplate(
                moduleKey,
                packageDSDocuments,
                subModuleKey
            )
        },
        "hasObligatedMandateTemplate": {
            ...(_initValueOfInput("hasObligatedMandateTemplate")),
            isRequired: moduleKey === c.DS_MODULE_CLIENT_CONTRACT_PREM,
            errorMessage: _("noObligatedMandateTemplateSelected"),
            errorMessageRequired: _("noObligatedMandateTemplateSelected"),
            isValid: (value) => !(moduleKey == c.DS_MODULE_CLIENT_CONTRACT_PREM && !value),
            value: isMandateAndHasAMarkedTemplate(
                moduleKey,
                packageDSDocuments,
                subModuleKey
            )
        },
        "firstSignerFirstName": {
            ...(_initValueOfInput("firstSignerFirstName")),
            isRequired: isSignable,
            errorMessage: _("invalidFirstSignerFirstName"),
            value: isSignable ? (firstSigningStakeholder ? firstSigningStakeholder.firstName : nesProp.get(formInitialValues, 'firstSignerFirstName')) : ''
        },
        "firstSignerLastName": {
            ...(_initValueOfInput("firstSignerLastName")),
            isRequired: isSignable,
            errorMessage: _("invalidFirstSignerLastName"),
            value: isSignable ? (firstSigningStakeholder ? firstSigningStakeholder.lastName : nesProp.get(formInitialValues, 'firstSignerLastName')) : ''
        },
        "firstSignerPhoneNumber": {
            ...(_initValueOfInput("firstSignerPhoneNumber")),
            isRequired: requireSmsOtpSigning,
            errorMessage: _("invalidPhoneNumber"),
            isValid: (value) => (value[0] == '0' ? value.length === 10 : value.length === 9),
            value: requireSmsOtpSigning ? (firstSigningStakeholder ? spreadPhoneNumber(firstSigningStakeholder.phoneNumber).phoneNumber : nesProp.get(formInitialValues, 'firstSignerPhoneNumber')) : ''
        },
        "firstSignerCountryPhoneCode": {
            ...(_initValueOfInput("firstSignerCountryPhoneCode")),
            isRequired: requireSmsOtpSigning,
            errorMessage: _("invalidPhoneNumberCode"),
            value: requireSmsOtpSigning ? (firstSigningStakeholder ? spreadPhoneNumber(firstSigningStakeholder.phoneNumber).phoneCode : nesProp.get(formInitialValues, 'firstSignerCountryPhoneCode')) : ''
        },

        // second signer fields
        "secondSignerLastName": {
            ...(_initValueOfInput("secondSignerLastName")),
            isRequired: false,
            errorMessage: _("invalidSecondSignerLastName"),
            value: secondSigningStakeholder ? secondSigningStakeholder.lastName : ''
        },
        "secondSignerFirstName": {
            ...(_initValueOfInput("secondSignerFirstName")),
            isRequired: false,
            errorMessage: _("invalidSecondSignerFirstName"),
            value: secondSigningStakeholder ? secondSigningStakeholder.firstName : ''
        },
        "secondSignerEmailAddress": {
            ...(_initValueOfInput("secondSignerEmailAddress")),
            isRequired: false,
            errorMessage: _("invalidSecondSignerEmailAddress"),
            isValid: (value) => (value && isAnEmail(value)),
            value: secondSigningStakeholder ? secondSigningStakeholder.email : ''
        },
        "secondSignerPhoneNumber": {
            ...(_initValueOfInput("secondSignerPhoneNumber")),
            isRequired: false,
            errorMessage: _("invalidSecondSignerPhoneNumber"),
            isValid: (value) => (value[0] == '0' ? value.length === 10 : value.length === 9),
            value: secondSigningStakeholder ? spreadPhoneNumber(secondSigningStakeholder.phoneNumber).phoneNumber : ''
        },
        "secondSignerCountryPhoneCode": {
            ...(_initValueOfInput("secondSignerCountryPhoneCode")),
            isRequired: false,
            errorMessage: _("secondSignerCountryPhoneCode"),
            value: secondSigningStakeholder ? spreadPhoneNumber(secondSigningStakeholder.phoneNumber).phoneCode : ''
        },
        "secondSignerLanguage": {
            ...(_initValueOfInput("secondSignerLanguage")),
            isRequired: false,
            errorMessage: _("invalidSecondSignerLanguage"),
            value: secondSigningStakeholder ? secondSigningStakeholder.language : nesProp.get(formInitialValues, 'clientLanguage')
        },

        // broker signer fields
        "brokerSignerExists": {
            ...(_initValueOfInput("brokerSignerExists")),
            isRequired: false,
            errorMessage: _("brokerSignerExists"),
            value: brokerSigningStakeholder ? true : false
        },
        "brokerSignerLastName": {
            ...(_initValueOfInput("brokerSignerLastName")),
            isRequired: false,
            errorMessage: _("invalidBrokerSignerLastName"),
            value: brokerSigningStakeholder ? brokerSigningStakeholder.lastName : ''
        },
        "brokerSignerFirstName": {
            ...(_initValueOfInput("brokerSignerFirstName")),
            isRequired: false,
            errorMessage: _("invalidBrokerSignerFirstName"),
            value: brokerSigningStakeholder ? brokerSigningStakeholder.firstName : ''
        },
        // "brokerSignerBirthDate": {...(_initValueOfInput("brokerSignerBirthDate")),
        //     isRequired: false,
        //     errorMessage: _("invalidBrokerSignerBirthDate"),
        //     value: brokerSigningStakeholder ? brokerSigningStakeholder.birthDate : moment().subtract(18, 'years').format("YYYY/MM/DD")
        // },
        "brokerSignerLanguage": {
            ...(_initValueOfInput("brokerSignerLanguage")),
            isRequired: false,
            errorMessage: _("invalidBrokerSignerLanguage"),
            value: brokerSigningStakeholder ? brokerSigningStakeholder.language : nesProp.get(formInitialValues, 'clientLanguage')
        },
        "brokerSignerPhoneNumber": {
            ...(_initValueOfInput("brokerSignerPhoneNumber")),
            isRequired: false,
            errorMessage: _("invalidBrokerSignerPhoneNumber"),
            isValid: (value) => (value[0] == '0' ? value.length === 10 : value.length === 9),
            value: brokerSignerPhoneNumber
        },
        "brokerSignerCountryPhoneCode": {
            ...(_initValueOfInput("brokerSignerCountryPhoneCode")),
            isRequired: false,
            errorMessage: _("invalidBrokerSignerCountryPhoneCode"),
            value: brokerSignerCountryPhoneCode
        },

        ...dynamicInputFieldList
    }
}

/**
 * This function helps to find by a input-select-value a full element from options, so a value-label pair.
 * This is necessary for react select 2 to display the label of the selected option after selection in dropdown list.
 * @param {*} value
 * @param {*} options
 */
export const findItemByValueFromSelectOptions = (value, options) => {
    let result = { label: value, value: value }

    if (!options) {
        return result
    }

    let filteredByValue = options.filter((item) => {
        return item.value === value
    })

    // if no result in list of options the result the subject value
    if (!filteredByValue || filteredByValue.length === 0) {
        return result
    }

    // in case of match then the first match is the selected one. If there are no values then remains the subject value
    return nesProp.get(filteredByValue, "0") || result
}

/**
 * This functino will remove all of the document related inputs from input list
 * @param {*} formInputs
 * @param {*} DSDocumentOriginTypes
 * @returns
 */
export const clearPackageDSDocumentsInputsFromForm = (formInputs, DSDocumentOriginTypes) => {
    // get not indexed filed list
    let initialDocumentFields = getDynamicInputFieldByCursor({ cursor: "" }, "", "", DSDocumentOriginTypes)

    // first check the physical inputs for values
    for (const formFieldName in formInputs) {

        let isItDocumentRelatedInput = false

        // check with regexp, the not indexed field names used with a regexp can
        // detect an already generated field name
        for (const oneName in initialDocumentFields) {
            const regex = new RegExp(`${oneName}\\d+$`);
            if (regex.test(formFieldName)) {


                isItDocumentRelatedInput = true
            }
        }

        if (isItDocumentRelatedInput) {
            delete formInputs[formFieldName]
        }
    }

    return formInputs
}

/**
 * This function will regenerate the form inputs for dynamic list of documents, without touching other form fields.
 * This is useful to refresh the form inputs after updating the package in Mongodb, after that can be update the form.
 * Make sure before call this function, the document values to be saved in the document list, because the
 * the form inputs hold the current values from form, that will be overwrite by this method.
 * @param {*} formInputs
 * @param {*} packageDSDocuments
 * @param {*} moduleKey
 * @param {*} subModuleKey
 * @param {*} DSDocumentOriginTypes
 */
export const synchronizePackageDSDocumentsInputsFromForm = (formInputs, packageDSDocuments, moduleKey, subModuleKey, DSDocumentOriginTypes) => {
    formInputs = clearPackageDSDocumentsInputsFromForm(formInputs, DSDocumentOriginTypes)

    // generate and attach the list of documents inputs
    let dynamicInputFieldList = getFormDynamicInputFieldsList(packageDSDocuments, moduleKey, subModuleKey, DSDocumentOriginTypes)
    formInputs = {
        ...formInputs,
        ...dynamicInputFieldList
    }

    return formInputs
}



// WEBPACK FOOTER //
// ./components/DSDocuments/DSWizardFormControl.jsx